import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { BookingForm } from "../components/BookingForm";
import { RENTAL_FIELDS, CHAUFFEUR_FIELDS, validateBooking } from "./bookingFields";
import { PencilSimple, ArrowUUpLeft, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function RejectedBookings() {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [chauffeurs, setChauffeurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formType, setFormType] = useState("rental");
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, v, c] = await Promise.all([
        api.get("/bookings", { params: { status: "rejected", mine: true } }),
        api.get("/vehicles"),
        api.get("/chauffeurs"),
      ]);
      setRows(b.data); setVehicles(v.data); setChauffeurs(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openEdit = (b) => {
    setFormType(b.booking_type === "rental" ? "rental" : "chauffeur");
    setEditing(b);
    setForm({ ...b });
  };

  const handleVehicleChange = (id) => {
    const v = vehicles.find((x) => x.id === id);
    setForm((f) => ({ ...f, vehicle_id: id, vehicle_label: v ? `${v.make} ${v.model}${v.year ? " " + v.year : ""}` : "", vehicle_plate: v?.license_plate || "" }));
  };
  const handleDriverChange = (id) => {
    const d = chauffeurs.find((x) => x.id === id);
    setForm((f) => ({ ...f, driver_id: id, driver_name: d ? `${d.first_name} ${d.last_name}` : "" }));
  };

  const buildPayload = () => {
    const payload = { ...form, booking_type: formType };
    const fields = formType === "rental" ? RENTAL_FIELDS : CHAUFFEUR_FIELDS;
    for (const f of fields) if (f.type === "number" && payload[f.name] !== "" && payload[f.name] !== null) payload[f.name] = Number(payload[f.name]) || 0;
    return payload;
  };

  const saveOnly = async () => {
    const err = validateBooking(form, formType);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.put(`/bookings/${editing.id}`, buildPayload());
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const saveAndResubmit = async () => {
    const err = validateBooking(form, formType);
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.put(`/bookings/${editing.id}`, buildPayload());
      await api.post(`/bookings/${editing.id}/transition`, null, { params: { new_status: "submitted" } });
      toast.success("Resubmitted for review");
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const resubmit = async (id) => {
    try {
      await api.post(`/bookings/${id}/transition`, null, { params: { new_status: "submitted" } });
      toast.success("Resubmitted for review");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="rejected-bookings-page">
      <div className="label-mono">Bookings sent back for edits</div>
      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left px-4 py-3.5 label-mono font-normal">Client</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Vehicle</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Period</th>
              <th className="text-left px-4 py-3.5 label-mono font-normal">Reason sent back</th>
              <th className="text-right px-4 py-3.5 label-mono font-normal w-64">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="p-12 text-center text-muted-foreground">Loading…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={5} className="p-12 text-center text-muted-foreground">No bookings sent back. You're all caught up.</td></tr>
                : rows.map((b) => (
                  <tr key={b.id} className="border-b border-border/50 last:border-b-0 hover:bg-white/[0.03] transition-colors" data-testid={`rejected-row-${b.id}`}>
                    <td className="px-4 py-4"><div className="font-heading text-base">{b.customer_name}</div><div className="label-mono mt-0.5">{b.booking_type}</div></td>
                    <td className="px-4 py-4 text-muted-foreground">{b.vehicle_label || "—"}</td>
                    <td className="px-4 py-4 text-xs tnum text-muted-foreground">{b.start_datetime ? new Date(b.start_datetime).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}</td>
                    <td className="px-4 py-4 text-xs max-w-xs">
                      {b.rejection_reason
                        ? <span className="inline-flex items-start gap-1.5 text-amber-400" data-testid={`rejected-reason-${b.id}`}><Warning size={14} className="mt-0.5 shrink-0" />{b.rejection_reason}</span>
                        : <span className="text-muted-foreground">— (no reason given)</span>}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="rounded-full mr-1.5" onClick={() => openEdit(b)} data-testid={`rejected-edit-${b.id}`}><PencilSimple size={12} className="mr-1" />Edit</Button>
                      <Button size="sm" variant="ghost" className="rounded-full" onClick={() => resubmit(b.id)} data-testid={`rejected-resubmit-${b.id}`}><ArrowUUpLeft size={14} className="mr-1 text-primary" />Resubmit</Button>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-border" data-testid="rejected-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit {formType === "rental" ? "Rental" : "Chauffeur"} Booking</DialogTitle>
            <DialogDescription>Make changes, then save or resubmit for admin review.</DialogDescription>
          </DialogHeader>
          {editing?.rejection_reason && (
            <div className="border border-amber-500/40 bg-amber-500/10 text-amber-300 text-sm px-4 py-2 rounded-sm flex items-start gap-2">
              <Warning size={16} className="mt-0.5 shrink-0" /><span><b>Sent back:</b> {editing.rejection_reason}</span>
            </div>
          )}
          {editing && (
            <BookingForm
              formType={formType}
              form={form}
              setForm={setForm}
              vehicles={vehicles}
              chauffeurs={chauffeurs}
              onVehicleChange={handleVehicleChange}
              onDriverChange={handleDriverChange}
              excludeBookingId={editing.id}
            />
          )}
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button variant="outline" onClick={saveOnly} disabled={saving} data-testid="rejected-save-only">Save changes</Button>
            <Button onClick={saveAndResubmit} disabled={saving} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="rejected-save-resubmit">{saving ? "Saving…" : "Save & Resubmit"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
